import React, {
  useEffect,
  useMemo,
  useReducer,
  useState,
  cloneElement,
} from "react";
import clsx from "clsx";
import keyBy from "lodash/keyBy";
import { Form, Steps } from "antd";
import { useParams } from "react-router-dom";
import styles from "./AddNewCampaign.module.css";
import GenericPageHeader from "../../shared/GenericPageHeader/GenericPageHeader";
import ProductStep from "./ProductStep";
import DetailsStep from "./DetailsStep";
import FlowStep from "./FlowStep";
import GenericStep from "./GenericStep";
import InstagramStep from "./InstagramStep";
import TiktokStep from "./TiktokStep";
import SubmitStep from "./SubmitStep";
import { useCompany } from "../../../api/companies";
import { useCampaignFlows } from "../../../api/campaignFlows";
import { useCampaignSteps } from "../../../api/campaignSteps";
import { useCategories } from "../../../api/categories";
import { useEmailTemplates } from "../../../api/emailTemplates";
import { useContractById } from "../../../api/contracts";
import RequirementsStep from "./RequirementsStep";
import { useCampaignRequirements } from "../../../api/campaignRequirements";
import { useProductPlatforms } from "../../../api/productPlatforms";
import { FLOW_STEP_TYPES } from "../../../utils/campaignFlowTypes";
import { EmailTemplatePreviewProvider } from "./EmailTemplatePreviewContext";

const { Step } = Steps;

const reducer = (state, newState) => ({
  ...state,
  ...newState,
});

const getStepData = (data, stepName) => {
  const campaignStep = JSON.parse(data.values.campaignStep);
  const step = {
    ...campaignStep,
  };
  if (!campaignStep.campaignStepId) {
    step.emailTemplateFlow = JSON.parse(campaignStep.emailTemplateFlow);
  }
  return { [stepName]: step };
};

// Generate flow form handlers dynamically
const generateFlowFormHandlers = () =>
  FLOW_STEP_TYPES.reduce((acc, { name }) => {
    acc[`${name}Form`] = data => getStepData(data, `${name}Step`);
    return acc;
  }, {});

//determines how to handle form data based on specific form
const dataHandler = {
  productForm: data => ({
    product: JSON.parse(data.values.product),
  }),
  detailsForm: data => ({
    details: { ...data.values },
  }),
  requirementsForm: data => ({
    campaignRequirements: JSON.parse(data.values.campaignRequirements),
  }),
  flowForm: data => ({
    campaignFlow: JSON.parse(data.values.campaignFlow),
  }),
  // Dynamically generated handlers for all flow steps
  ...generateFlowFormHandlers(),
};

// The selected product's platform, e.g. Amazon, once the product and platforms have loaded
const getProductPlatform = (productPlatforms, product) =>
  productPlatforms?.[product?.productPlatformId];

// The html of the platform's review template, which new review templates start from. Undefined for
// platforms without one (brand website), or before the email templates have loaded.
const getPlatformReviewHtml = (emailTemplates, productPlatform) =>
  emailTemplates?.[productPlatform?.reviewEmailTemplateId]?.html;

// Example placeholder values for the email template forms, from what's known while creating the
// campaign. The onboard and the campaign's ID don't exist yet, so they're left out.
const getEmailTemplatePreview = ({
  allData,
  campaignState,
  contract,
  company,
}) => {
  const productPlatform = getProductPlatform(
    allData.productPlatforms,
    campaignState?.product
  );
  // The full product, with fields like thumbnailUrl that the product step doesn't keep
  const product =
    allData.products?.[campaignState?.product?.productId] ||
    campaignState?.product;
  const values = {
    campaign: {
      name: campaignState?.details?.name,
      link: campaignState?.details?.link,
      contractId: contract?.contractId,
    },
    product,
    contract,
    company,
  };

  return {
    values,
    platformReviewHtml: getPlatformReviewHtml(
      allData.emailTemplates,
      productPlatform
    ),
  };
};

function AddNewCampaign() {
  const { contractId, companyId } = useParams();

  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);

  // Setup forms
  const [requirementsForm] = Form.useForm();
  const [productForm] = Form.useForm();
  const [detailsForm] = Form.useForm();
  const [flowForm] = Form.useForm();
  const [orderForm] = Form.useForm();
  const [tiktokForm] = Form.useForm();
  const [instagramForm] = Form.useForm();
  const [reviewForm] = Form.useForm();

  const flowForms = {
    order: orderForm,
    tiktok: tiktokForm,
    instagram: instagramForm,
    review: reviewForm,
  };

  // Initialize visibility state for all flow steps
  const [visibleFlowSteps, setVisibleFlowSteps] = useState(
    FLOW_STEP_TYPES.reduce((acc, { name }) => {
      acc[name] = false;
      return acc;
    }, {})
  );

  const [allData, setAllData] = useReducer(reducer, {});

  useProductPlatforms({
    onSuccess: productPlatforms => {
      setAllData({
        productPlatforms: keyBy(productPlatforms, "productPlatformId"),
      });
    },
  });

  const { data: contract } = useContractById({
    contractId,
    includes: ["campaigns", "products", "companies"],
    params: { stats: true },
  });

  const { data: company, isLoading: isLoadingCompany } = useCompany({
    companyId,
    includes: ["products"],
    onSuccess: _company => {
      setAllData({
        products: keyBy(_company.relations.products, "productId"),
      });
    },
  });

  const { isLoading: isLoadingCampaignFlows } = useCampaignFlows({
    includes: ["campaignSteps"],
    onSuccess: campaignFlows => {
      setAllData({
        campaignFlows: keyBy(campaignFlows, "campaignFlowId"),
      });
    },
  });

  const { isLoading: isLoadingCampaignSteps } = useCampaignSteps({
    params: {
      sortColumn: "name",
      sortDirection: "asc",
    },
    onSuccess: campaignSteps => {
      setAllData({
        campaignSteps: keyBy(campaignSteps, "campaignStepId"),
      });
    },
  });

  const { isLoading: isLoadingCampaignRequirements } = useCampaignRequirements({
    params: {
      sortColumn: "name",
      sortDirection: "asc",
    },
    onSuccess: campaignRequirements => {
      setAllData({
        campaignRequirements: keyBy(
          campaignRequirements,
          "campaignRequirementId"
        ),
      });
    },
  });

  useCategories({
    params: {
      sortColumn: "categories.name",
      sortDirection: "asc",
    },
    onSuccess: categories => {
      setAllData({
        categories: keyBy(
          categories.map(category => ({
            ...category,
            children: keyBy(category?.children || [], "categoryId"),
          })),
          "categoryId"
        ),
      });
    },
  });

  useEmailTemplates({
    onSuccess: emailTemplates => {
      setAllData({
        emailTemplates: keyBy(emailTemplates, "emailTemplateId"),
        requiredEmailTemplates: keyBy(
          emailTemplates.filter(
            template =>
              template.template ===
              process.env.REACT_APP_SENDGRID_REQUIRED_REMINDER
          ),
          "emailTemplateId"
        ),
        optionalEmailTemplates: keyBy(
          emailTemplates.filter(
            template =>
              template.template ===
              process.env.REACT_APP_SENDGRID_OPTIONAL_REMINDER
          ),
          "emailTemplateId"
        ),
      });
    },
  });

  const addEmailTemplate = (emailTemplate, stepType) => {
    const emailTemplatesData = {
      emailTemplates: {
        [emailTemplate.name]: emailTemplate,
        ...allData.emailTemplates,
      },
    };
    if (
      stepType === "order" ||
      stepType === "instagram" ||
      stepType === "tiktok"
    ) {
      emailTemplatesData.requiredEmailTemplates = {
        [emailTemplate.name]: emailTemplate,
        ...allData.requiredEmailTemplates,
      };
    } else if (stepType === "review") {
      emailTemplatesData.optionalEmailTemplates = {
        [emailTemplate.name]: emailTemplate,
        ...allData.optionalEmailTemplates,
      };
    }
    setAllData(emailTemplatesData);
  };

  const [campaignState, setCampaignState] = useReducer(reducer, {});

  const emailTemplatePreview = useMemo(
    () =>
      getEmailTemplatePreview({ allData, campaignState, contract, company }),
    [allData, campaignState, contract, company]
  );

  const [error, setError] = useState("");

  const [navigationDisabled, setNavigationDisabled] = useReducer(reducer, {});

  const onBack = () => {
    updateStep(currentStep - 1);
  };

  const onFormDisabled = disabled => {
    //if the current form is disabled (due to the second form of the TwoComponentSwitch showing), disallow step navigation
    setNavigationDisabled({ [currentStep]: disabled });
    if (!disabled) {
      setError("");
    }
  };

  const updateStep = async step => {
    if (step < currentStep) {
      setCurrentStep(step);
      setError("");
      return;
    }
    // when updating to a particular step ahead of the current step, iterate over the forms leading up to that step to check if they are valid
    // if any one isn't valid, it will stop at that particular step prompting the user to fill out the required missing/invalid fields
    for (let i = currentStep; i < step; i++) {
      const { form } = steps[i];
      if (navigationDisabled[i]) {
        //this will happen if the second component of the TwoComponentSwitch is visible and the user tries to move forward using the steps
        setError("You must submit this form to continue.");
        return;
      }
      if (form) {
        try {
          await form.validateFields();
          const formValues = form.getFieldsValue(true);
          if (Object.keys(formValues).length === 0) {
            setError("You must submit this form to continue.");
            return;
          }
          form.submit();
        } catch (error) {
          return;
        }
      } else {
        setError("");
        goToNextStep();
      }
    }
  };

  // Map a flow to the component we want to display and what type of email templates it uses
  function getFlowStepConfig(stepName) {
    const baseStep = FLOW_STEP_TYPES.find(step => step.name === stepName);

    if (!baseStep) {
      return null;
    }

    let component;
    let emailTemplateType;

    // Add specific mappings based on step name
    switch (stepName) {
      case "instagram":
        component = InstagramStep;
        emailTemplateType = "required";
        break;
      case "tiktok":
        component = TiktokStep;
        emailTemplateType = "required";
        break;
      case "review":
        component = GenericStep;
        emailTemplateType = "optional";
        break;
      case "order":
      default:
        component = GenericStep;
        emailTemplateType = "required";
        break;
    }

    return {
      ...baseStep,
      component,
      emailTemplateType,
    };
  }

  const stepsContent = [
    {
      visible: true,
      component: (
        <ProductStep
          form={productForm}
          onFormDisabled={onFormDisabled}
          companyId={companyId}
          products={allData.products}
          loading={isLoadingCompany}
          categories={allData.categories}
          productPlatforms={allData.productPlatforms}
        />
      ),
    },
    {
      visible: true,
      component: (
        <DetailsStep
          form={detailsForm}
          onBack={onBack}
          productName={campaignState?.product?.name}
          productCode={campaignState?.product?.code}
          contractCampaigns={contract?.relations?.campaigns}
          productPlatform={getProductPlatform(
            allData.productPlatforms,
            campaignState?.product
          )}
        />
      ),
    },
    {
      visible: true,
      component: (
        <RequirementsStep
          form={requirementsForm}
          onFormDisabled={onFormDisabled}
          companyId={companyId}
          requirements={allData.campaignRequirements}
          loading={isLoadingCampaignRequirements}
          contractName={contract?.name}
        />
      ),
    },
    {
      visible: true,
      component: (
        <FlowStep
          form={flowForm}
          campaignFlows={allData.campaignFlows}
          onBack={onBack}
          onFormDisabled={onFormDisabled}
          contractName={contract?.name}
          loading={isLoadingCampaignFlows}
        />
      ),
    },
    // Generate the other flow step components
    ...FLOW_STEP_TYPES.map(({ name }) => {
      const stepConfig = getFlowStepConfig(name);
      const { component: StepComponent, emailTemplateType } = stepConfig;

      const form = flowForms[name];

      // Because this is dynamically generated, check that the form exists
      // Throwing an error here loops indefinitely, so just error to console
      if (!form) {
        console.error(`No form found for flow step: ${name}`);
      }

      const sharedProperties = {
        stepType: name,
        steps,
        form,
        onBack,
        onFormDisabled,
        contractName: contract?.name,
        campaignSteps: allData.campaignSteps,
        loading: isLoadingCampaignSteps,
        companyName: company?.name,
        // Picks the platform's default email templates
        platform: getProductPlatform(
          allData.productPlatforms,
          campaignState?.product
        )?.name,
        link: campaignState.details?.link,
        addEmailTemplate,
      };

      return {
        visible: visibleFlowSteps[name],
        component: (
          <StepComponent
            {...sharedProperties}
            emailTemplates={
              emailTemplateType === "required"
                ? allData.requiredEmailTemplates
                : allData.optionalEmailTemplates
            }
          />
        ),
      };
    }),
    {
      visible: true,
      component: (
        <SubmitStep
          campaignState={campaignState}
          allData={allData}
          contractId={contractId}
          companyId={companyId}
          updateStep={updateStep}
        />
      ),
    },
  ];

  const getStepStatus = step => {
    if (currentStep === step) {
      return "process";
    }
    if (currentStep > step) {
      return "finish";
    }
    return "wait";
  };

  useEffect(() => {
    const newSteps = [
      {
        title: "Product",
        form: productForm,
        status: "process",
        visible: true,
      },
      {
        title: "Details",
        form: detailsForm,
        status: "wait",
        visible: true,
      },
      {
        title: "Requirements",
        form: requirementsForm,
        status: "wait",
        visible: true,
      },
      {
        title: "Flow",
        status: "wait",
        form: flowForm,
        visible: true,
      },
      // Add flow steps for remaining flow steps
      ...FLOW_STEP_TYPES.map(({ name, title }) => ({
        title: `${title} Step`,
        status: "wait",
        form: flowForms[name],
        visible: visibleFlowSteps[name],
      })),
      {
        title: "Submit",
        status: "wait",
        visible: true,
      },
    ];

    newSteps
      .filter(step => step.visible)
      .forEach((step, i) => {
        step.status = getStepStatus(i);
      });

    setSteps(newSteps);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFlowSteps, currentStep]);

  const goToNextStep = () => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
  };

  const goToNextFlowStep = flowData => {
    //new flow
    const newVisibleFlowSteps = FLOW_STEP_TYPES.reduce((acc, { name }) => {
      acc[name] = false;
      return acc;
    }, {});

    if (!flowData.campaignFlowId) {
      const flowSteps = [];
      flowSteps.push(...flowData.steps.map(f => f.name));

      for (const flowStep of flowSteps) {
        newVisibleFlowSteps[flowStep] = true;
      }
    }
    const newCampaignState = { ...campaignState };
    const flowStepKeys = Object.keys(newVisibleFlowSteps);
    for (const flowStepKey of flowStepKeys) {
      if (!newVisibleFlowSteps[flowStepKey]) {
        newCampaignState[`${flowStepKey}Step`] = undefined;
      }
    }
    setCampaignState(newCampaignState);
    setVisibleFlowSteps(newVisibleFlowSteps);
    goToNextStep();
  };

  const onNext = async (formName, data) => {
    //this will automatically be triggered each time any form within the overall campaign creation UI is submitted
    const nextStep = currentStep + 1;
    if (nextStep < steps.length) {
      const formData = { ...dataHandler[formName](data) };
      if (formName === "flowForm") {
        goToNextFlowStep(formData.campaignFlow);
      } else {
        goToNextStep();
      }
      setCampaignState(formData);
    }
  };

  return (
    <EmailTemplatePreviewProvider value={emailTemplatePreview}>
      <GenericPageHeader
        title={
          campaignState.details?.name
            ? `New Campaign - ${campaignState.details?.name}`
            : "Create New Campaign"
        }
        showBreadcrumb={false}
      />
      <div className={styles.container}>
        <Steps
          onChange={updateStep}
          current={currentStep}
          className={styles.steps}
          labelPlacement="vertical"
        >
          {steps
            .filter(step => step.visible)
            .map(step => (
              <Step status={step.status} key={step.title} title={step.title} />
            ))}
        </Steps>

        <div className={styles.error}>{error}</div>

        <Form.Provider onFormFinish={onNext}>
          {stepsContent
            .filter(step => step.visible)
            .map((step, i) => (
              <div
                key={i}
                className={clsx({
                  [styles.visible]: currentStep === i,
                  [styles.hidden]: currentStep !== i,
                })}
              >
                {cloneElement(step.component, { visible: currentStep === i })}
              </div>
            ))}
        </Form.Provider>
      </div>
    </EmailTemplatePreviewProvider>
  );
}

export default AddNewCampaign;
